import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Card, Badge, Btn } from "../shared/components"
import { Modal, ConfirmModal, inputStyle, labelStyle } from "../shared/Modal"
import { createProgram, updateProgram, listEnrollments } from "../api/programs.api"
import { tierLabel } from "../lib/tier"

/* ============================================================
   Kelola Program — hanya SUPER_ADMIN (admin lembaga).

   Program tidak harus OPRC: tingkat opsional (kosongkan untuk program tanpa
   tingkat), label tingkat memakai istilah lembaga sendiri, dan warna serta
   ikonnya dipilih di sini. Program yang dinonaktifkan diarsipkan: peserta dan
   trainer tidak lagi melihat atau membukanya dan pendaftaran baru ditolak,
   tetapi SUPER_ADMIN tetap melihatnya dan sertifikat peserta tidak terpengaruh.
   ============================================================ */

// Hex #RRGGBB saja: itulah satu-satunya bentuk yang dipakai tema (AppContext) dan
// yang diterima server; nilai lain tampak tersimpan padahal diabaikan.
const PALET = ["#0F62FE", "#1F7A5C", "#7A6A45", "#B84E2A", "#6B4FA0", "#3E4A50", "#B53A2C", "#0E7C86"]
const WARNA_BAWAAN = PALET[0]
const HEX = /^#[0-9a-fA-F]{6}$/

// Hanya nama yang memang ada di komponen Icon.
const IKON = ["shield", "compass", "anchor", "book", "layers", "flag", "star", "globe", "users", "cert", "map_pin", "calendar", "chart", "pie", "key", "bookmark"]

// "Keselamatan Kerja (K3)" -> "keselamatan-kerja-k3": bentuk yang diterima server
// (huruf kecil, angka, tanda hubung) dan yang akan tercetak di nomor sertifikat.
function slugify(teks) {
  return String(teks || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "")
}
const KODE_VALID = /^[a-z0-9]+(-[a-z0-9]+)*$/

const tr = (lang, id, en) => (lang === "id" ? id : en)

// Pesan galat server -> pesan untuk satu kolom, atau banner bila bukan milik kolom mana pun.
function petakanGalat(ex, lang) {
  const data = ex?.response?.data
  const status = ex?.response?.status
  if (status === 409 && data?.code === "PROGRAM_CODE_TAKEN") {
    return { fields: { code: tr(lang, "Kode ini sudah dipakai program lain di lembaga Anda.", "This code is already used by another program in your institution.") } }
  }
  if (status === 409 && data?.code === "PLAN_LIMIT_REACHED") {
    return { banner: data.error } // pesan Indonesia dari server: menyebut batas dan paketnya
  }
  if (status === 400 && Array.isArray(data?.details)) {
    const fields = {}
    for (const d of data.details) {
      const k = d.path?.[0]
      if (k && !fields[k]) fields[k] = d.message
    }
    if (Object.keys(fields).length) return { fields }
  }
  return { banner: data?.error || ex?.message || tr(lang, "Gagal menyimpan program.", "Could not save the program.") }
}

// ── Pratinjau: bagaimana program tampil di pemilih program ───────
function Pratinjau({ nama, kode, warna, ikon, tingkat }) {
  const { lang } = useApp()
  return (
    <div className="row gap-2" style={{ alignItems: "center", padding: "8px 10px", background: "var(--surface-2)", borderRadius: 8 }}
      aria-label={tr(lang, "Pratinjau program", "Program preview")}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: HEX.test(warna) ? warna : "var(--border-strong)", flexShrink: 0 }} />
      {ikon && <Icon name={ikon} size={14} color={HEX.test(warna) ? warna : "var(--text-3)"} />}
      <div style={{ minWidth: 0 }}>
        <div className="small" style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {(kode || "kode").toUpperCase()}{tingkat ? ` · ${tingkat}` : ""}
        </div>
        <div className="tiny muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nama || tr(lang, "Nama program", "Program name")}</div>
      </div>
    </div>
  )
}

// ── Formulir membuat / mengubah program ──────────────────────────
function ProgramFormModal({ program, onClose, onSaved }) {
  const { lang, refreshPrograms, setCurrentProgramId } = useApp()
  const isEdit = !!program

  const [name, setName] = React.useState(program?.name || "")
  const [code, setCode] = React.useState(program?.code || "")
  // Selama kode belum disentuh orang, ia mengikuti nama; begitu diketik sendiri, tidak lagi ditimpa.
  const [codeTouched, setCodeTouched] = React.useState(false)
  const [description, setDescription] = React.useState(program?.description || "")
  const [hasTier, setHasTier] = React.useState(program ? program.level != null : false)
  const [level, setLevel] = React.useState(program?.level != null ? String(program.level) : "1")
  const [levelLabel, setLevelLabel] = React.useState(program?.level_label || "")
  const [color, setColor] = React.useState(program?.color_theme && HEX.test(program.color_theme) ? program.color_theme : WARNA_BAWAAN)
  const [icon, setIcon] = React.useState(program?.icon || "")
  const [busy, setBusy] = React.useState(false)
  const [fieldErr, setFieldErr] = React.useState({})
  const [banner, setBanner] = React.useState(null)

  // Galat dari server menempel pada kolomnya; begitu kolom itu disunting, galatnya
  // sudah tidak berlaku (mis. "kode sudah dipakai" setelah kodenya diganti) dan
  // harus hilang, bukan menumpuk di samping pesan validasi yang baru.
  const limpar = (...kunci) => setFieldErr((f) => (kunci.some((k) => f[k]) ? { ...f, ...Object.fromEntries(kunci.map((k) => [k, undefined])) } : f))

  function ubahNama(v) {
    setName(v)
    limpar("name")
    if (!isEdit && !codeTouched) { setCode(slugify(v)); limpar("code") }
  }

  const levelNum = Number(level)
  const levelValid = !hasTier || (Number.isInteger(levelNum) && levelNum >= 1)
  const codeValid = isEdit || (code.length >= 1 && code.length <= 60 && KODE_VALID.test(code))
  const bisaSimpan = name.trim().length > 0 && codeValid && levelValid && !busy
  const labelTingkat = hasTier && levelValid ? (levelLabel.trim() || `Level ${levelNum}`) : null

  async function submit(e) {
    e.preventDefault()
    if (!bisaSimpan) return
    setBusy(true); setFieldErr({}); setBanner(null)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        level: hasTier ? levelNum : null,
        level_label: hasTier ? (levelLabel.trim() || null) : null,
        color_theme: color,
        icon: icon || null,
      }
      let saved
      if (isEdit) saved = await updateProgram(program.id, payload)
      else saved = await createProgram({ ...payload, code })
      await refreshPrograms()
      // Program baru langsung dipilih: itulah yang hampir pasti dikerjakan berikutnya.
      if (!isEdit && saved?.id) setCurrentProgramId(saved.id)
      toast.success(isEdit ? tr(lang, "Program disimpan", "Program saved") : tr(lang, "Program dibuat", "Program created"))
      onSaved()
    } catch (ex) {
      const m = petakanGalat(ex, lang)
      setFieldErr(m.fields || {})
      setBanner(m.banner || null)
    } finally {
      setBusy(false)
    }
  }

  const galat = (k) => fieldErr[k] && <span role="alert" className="tiny" style={{ color: "var(--danger)" }}>{fieldErr[k]}</span>

  return (
    <Modal title={isEdit ? tr(lang, "Ubah Program", "Edit Program") : tr(lang, "Program Baru", "New Program")}
      onClose={busy ? () => {} : onClose} width={560}>
      <form onSubmit={submit} className="col gap-3">
        {banner && <div className="banner red" role="alert"><Icon name="alert" size={14} />{banner}</div>}

        <label className="col gap-1">
          <span style={labelStyle}>{tr(lang, "Nama program", "Program name")} *</span>
          <input style={inputStyle} value={name} maxLength={120} autoFocus required onChange={(e) => ubahNama(e.target.value)}
            placeholder={tr(lang, "mis. Keselamatan Kerja (K3)", "e.g. Occupational Safety")} />
          {galat("name")}
        </label>

        <label className="col gap-1">
          <span style={labelStyle}>{tr(lang, "Kode", "Code")} *</span>
          <input style={{ ...inputStyle, fontFamily: "var(--font-mono, monospace)", opacity: isEdit ? 0.7 : 1 }} value={code} maxLength={60}
            disabled={isEdit} required
            onChange={(e) => { setCodeTouched(true); setCode(e.target.value.toLowerCase()); limpar("code") }}
            placeholder="k3-dasar" />
          <span className="tiny muted">
            {isEdit
              ? tr(lang, "Kode tidak bisa diubah setelah program dibuat: ia tercetak di nomor sertifikat.", "The code cannot be changed after creation: it is printed on certificate numbers.")
              : tr(lang, "Huruf kecil, angka, dan tanda hubung. Tercetak di nomor sertifikat dan tidak bisa diubah nanti.", "Lowercase letters, digits and hyphens. Printed on certificate numbers and cannot be changed later.")}
          </span>
          {!isEdit && code && !codeValid && (
            <span role="alert" className="tiny" style={{ color: "var(--danger)" }}>
              {tr(lang, "Gunakan huruf kecil, angka, dan tanda hubung saja, tanpa spasi.", "Use lowercase letters, digits and hyphens only, without spaces.")}
            </span>
          )}
          {galat("code")}
        </label>

        <label className="col gap-1">
          <span style={labelStyle}>{tr(lang, "Deskripsi", "Description")}</span>
          <textarea style={{ ...inputStyle, height: 70, padding: 10 }} value={description} maxLength={2000} onChange={(e) => { setDescription(e.target.value); limpar("description") }} />
          {galat("description")}
        </label>

        <div className="col gap-2">
          <label className="row gap-2" style={{ alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={hasTier} onChange={(e) => setHasTier(e.target.checked)} />
            <span className="small" style={{ fontWeight: 500 }}>{tr(lang, "Program ini memiliki tingkat", "This program has tiers")}</span>
          </label>
          <span className="tiny muted">
            {tr(lang, "Biarkan mati untuk program tanpa tingkat (onboarding, kursus sekali jalan). Tingkat tidak dibatasi tiga.", "Leave off for programs without tiers (onboarding, one-off courses). Tiers are not limited to three.")}
          </span>
          {hasTier && (
            <div className="row gap-3" style={{ flexWrap: "wrap" }}>
              <label className="col gap-1">
                <span style={labelStyle}>{tr(lang, "Tingkat ke-", "Tier no.")}</span>
                <input style={{ ...inputStyle, width: 90 }} type="number" min="1" step="1" value={level} onChange={(e) => { setLevel(e.target.value); limpar("level") }} />
                {!levelValid && <span role="alert" className="tiny" style={{ color: "var(--danger)" }}>{tr(lang, "Minimal 1.", "At least 1.")}</span>}
                {galat("level")}
              </label>
              <label className="col gap-1" style={{ flex: 1, minWidth: 160 }}>
                <span style={labelStyle}>{tr(lang, "Sebutan tingkat (opsional)", "Tier name (optional)")}</span>
                <input style={inputStyle} value={levelLabel} maxLength={40} onChange={(e) => { setLevelLabel(e.target.value); limpar("level_label") }}
                  placeholder={tr(lang, "mis. Dasar", "e.g. Basic")} />
                {galat("level_label")}
              </label>
            </div>
          )}
        </div>

        <div className="col gap-1">
          <span style={labelStyle}>{tr(lang, "Warna", "Color")}</span>
          <div className="row gap-2" style={{ flexWrap: "wrap", alignItems: "center" }} role="radiogroup" aria-label={tr(lang, "Warna program", "Program color")}>
            {PALET.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={color.toLowerCase() === c.toLowerCase()} aria-label={c}
                onClick={() => { setColor(c); limpar("color_theme") }} title={c}
                style={{ width: 26, height: 26, borderRadius: "50%", background: c, cursor: "pointer",
                  border: color.toLowerCase() === c.toLowerCase() ? "3px solid var(--text)" : "2px solid var(--surface)",
                  outline: "1px solid var(--border-strong)" }} />
            ))}
            <label className="row gap-1 tiny muted" style={{ alignItems: "center", cursor: "pointer" }}>
              <input type="color" value={HEX.test(color) ? color : WARNA_BAWAAN} onChange={(e) => { setColor(e.target.value); limpar("color_theme") }}
                aria-label={tr(lang, "Warna lain", "Custom color")} style={{ width: 28, height: 28, padding: 0, border: 0, background: "none", cursor: "pointer" }} />
              {tr(lang, "lainnya", "custom")}
            </label>
          </div>
          {galat("color_theme")}
        </div>

        <div className="col gap-1">
          <span style={labelStyle}>{tr(lang, "Ikon", "Icon")}</span>
          <div className="row gap-1" style={{ flexWrap: "wrap" }} role="radiogroup" aria-label={tr(lang, "Ikon program", "Program icon")}>
            <button type="button" role="radio" aria-checked={icon === ""} onClick={() => { setIcon(""); limpar("icon") }}
              className="btn btn-ghost btn-sm" style={{ fontWeight: icon === "" ? 700 : 400, outline: icon === "" ? "2px solid var(--program-color)" : "none" }}>
              {tr(lang, "Tanpa ikon", "No icon")}
            </button>
            {IKON.map((n) => (
              <button key={n} type="button" role="radio" aria-checked={icon === n} aria-label={n} title={n} onClick={() => { setIcon(n); limpar("icon") }}
                className="btn btn-ghost btn-sm" style={{ outline: icon === n ? "2px solid var(--program-color)" : "none" }}>
                <Icon name={n} size={16} />
              </button>
            ))}
          </div>
          {galat("icon")}
        </div>

        <Pratinjau nama={name.trim()} kode={code} warna={color} ikon={icon} tingkat={labelTingkat} />

        <div className="row gap-2" style={{ justifyContent: "flex-end", marginTop: 4 }}>
          <Btn variant="ghost" type="button" disabled={busy} onClick={onClose}>{tr(lang, "Batal", "Cancel")}</Btn>
          <Btn variant="primary" type="submit" disabled={!bisaSimpan}>{busy ? "…" : tr(lang, "Simpan", "Save")}</Btn>
        </div>
      </form>
    </Modal>
  )
}

// ── Layar utama ──────────────────────────────────────────────────
export default function ProgramManageScreen() {
  const { lang, user, programs, refreshPrograms } = useApp()
  const orgName = useOrgName()
  const isMobile = useIsMobile()
  const [formFor, setFormFor] = React.useState(null) // {} = baru, program = ubah
  const [toDeactivate, setToDeactivate] = React.useState(null) // { program, enrolled }
  const [busyId, setBusyId] = React.useState(null)

  // Penegak sebenarnya tetap di server; ini hanya supaya layarnya tidak menawarkan yang tak bisa dilakukan.
  if (user?.role !== "SUPER_ADMIN") {
    return (
      <div className="main-inner">
        <div className="empty" style={{ padding: 40 }}>
          <Icon name="lock" size={28} color="var(--text-4)" />
          <div className="muted mt-2">{tr(lang, "Hanya admin lembaga yang dapat mengelola program.", "Only the institution admin can manage programs.")}</div>
        </div>
      </div>
    )
  }

  const daftar = programs || []

  // Dialog nonaktifkan menyebut berapa peserta yang terdampak, supaya akibatnya
  // terlihat sebelum ditekan, bukan sesudahnya.
  async function mintaNonaktif(p) {
    setBusyId(p.id)
    let enrolled = null
    try { enrolled = (await listEnrollments(p.id)).length } catch { /* jumlah tidak wajib */ }
    setBusyId(null)
    setToDeactivate({ program: p, enrolled })
  }

  async function setAktif(p, aktif) {
    setBusyId(p.id)
    try {
      await updateProgram(p.id, { is_active: aktif })
      await refreshPrograms()
      toast.success(aktif ? tr(lang, "Program diaktifkan", "Program activated") : tr(lang, "Program dinonaktifkan", "Program deactivated"))
      setToDeactivate(null)
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="main-inner" style={{ maxWidth: 900 }}>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div className="page-header-text">
          <div className="eyebrow">{orgName.toUpperCase()}</div>
          <h1 className="h1" style={{ marginTop: 6 }}>{tr(lang, "Kelola Program", "Manage Programs")}</h1>
          <div className="muted mt-1" style={{ fontSize: 13 }}>
            {tr(lang, "Buat dan atur program pelatihan lembaga Anda — tidak harus OPRC.", "Create and configure your institution's training programs — they need not be OPRC.")}
          </div>
        </div>
        <div className="page-header-actions">
          <Btn variant="primary" icon={<Icon name="plus" size={14} />} onClick={() => setFormFor({})}>{tr(lang, "Program baru", "New program")}</Btn>
        </div>
      </div>

      {daftar.length === 0 ? (
        <Card>
          <div className="empty" style={{ padding: 36 }}>
            <Icon name="layers" size={30} color="var(--text-4)" />
            <div className="muted mt-2">{tr(lang, "Belum ada program. Buat yang pertama.", "No programs yet. Create the first one.")}</div>
          </div>
        </Card>
      ) : (
        <div className="col gap-2">
          {daftar.map((p) => {
            const aktif = p.is_active !== false
            const tingkat = tierLabel(p)
            return (
              <Card key={p.id}>
                <div className="row gap-3" style={{ alignItems: "center", flexWrap: "wrap", opacity: aktif ? 1 : 0.75 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", background: HEX.test(p.color_theme || "") ? p.color_theme : "var(--border-strong)", flexShrink: 0 }} />
                  {p.icon && <Icon name={p.icon} size={16} color={HEX.test(p.color_theme || "") ? p.color_theme : "var(--text-3)"} />}
                  <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                    <div className="row gap-2" style={{ alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</span>
                      <Badge tone={aktif ? "success" : "default"} dot>{aktif ? tr(lang, "Aktif", "Active") : tr(lang, "Nonaktif", "Inactive")}</Badge>
                    </div>
                    <div className="tiny muted" style={{ marginTop: 2 }}>
                      <span className="mono">{p.code}</span>
                      {" · "}
                      {tingkat || tr(lang, "Tanpa tingkat", "No tiers")}
                    </div>
                  </div>
                  <div className="row gap-2" style={{ alignItems: "center", marginLeft: "auto", flexWrap: "wrap" }}>
                    <Btn variant="ghost" size="sm" icon={<Icon name="edit" size={12} />} onClick={() => setFormFor(p)}
                      aria-label={tr(lang, `Ubah ${p.name}`, `Edit ${p.name}`)}>
                      {isMobile ? null : tr(lang, "Ubah", "Edit")}
                    </Btn>
                    {aktif ? (
                      <Btn variant="ghost" size="sm" icon={<Icon name="lock" size={12} />} disabled={busyId === p.id} onClick={() => mintaNonaktif(p)}
                        aria-label={tr(lang, `Nonaktifkan ${p.name}`, `Deactivate ${p.name}`)}>
                        {isMobile ? null : tr(lang, "Nonaktifkan", "Deactivate")}
                      </Btn>
                    ) : (
                      <Btn variant="secondary" size="sm" icon={<Icon name="check" size={12} />} disabled={busyId === p.id} onClick={() => setAktif(p, true)}
                        aria-label={tr(lang, `Aktifkan ${p.name}`, `Activate ${p.name}`)}>
                        {isMobile ? null : tr(lang, "Aktifkan", "Activate")}
                      </Btn>
                    )}
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {formFor && (
        <ProgramFormModal
          program={formFor.id ? formFor : null}
          onClose={() => setFormFor(null)}
          onSaved={() => setFormFor(null)}
        />
      )}

      {toDeactivate && (
        <ConfirmModal
          title={tr(lang, "Nonaktifkan program?", "Deactivate program?")}
          message={tr(lang,
            `"${toDeactivate.program.name}" akan diarsipkan${toDeactivate.enrolled != null ? `, dan ${toDeactivate.enrolled} peserta terdaftar` : ", dan peserta terdaftarnya"} tidak lagi melihat atau membukanya. Trainer juga kehilangan akses. Pendaftaran baru ditolak dan pengingat terjadwal berhenti. Sertifikat yang sudah terbit tetap bisa dibuka. Anda bisa mengaktifkannya kembali kapan saja.`,
            `"${toDeactivate.program.name}" will be archived${toDeactivate.enrolled != null ? `, and its ${toDeactivate.enrolled} enrolled participants` : ", and its enrolled participants"} will no longer see or open it. Trainers lose access too. New enrollments are rejected and scheduled reminders stop. Certificates already issued stay available. You can reactivate it at any time.`)}
          confirmLabel={tr(lang, "Nonaktifkan", "Deactivate")}
          busy={busyId === toDeactivate.program.id}
          onConfirm={() => setAktif(toDeactivate.program, false)}
          onClose={() => setToDeactivate(null)}
        />
      )}
    </div>
  )
}
